import { useUserId } from "@/api/auth";
import { makeHomeQueryOptions } from "@/api/home";
import { infiniteSearchForEAPListingsQueryOptions, LocationType } from "@/api/listingSearch";
import { infinitePurchaseHistoryQueryOptions } from "@/api/purchases";
import { EAPListingCard, EAPListingCardSkeleton } from "@/components/EAPListingCard";
import { InfiniteScrollEnd } from "@/components/InfiniteScrollEnd";
import { ListingCardSkeleton } from "@/components/ListingCard";
import { PageInset } from "@/components/PageInset";
import { PurchaseDialog } from "@/components/PurchaseDialog";
import { ColoredCard } from "@/components/ui/ColoredCard";
import { ButtonLink, FlexLink } from "@/components/ui/Link";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { Shelf } from "@/components/ui/Shelf";
import { TopAppBar } from "@/components/ui/TopAppBar";
import { useMemberBenefits } from "@/hooks/useMemberBenefits";
import { generateSrcSet } from "@/utils/images";
import { captureCustomEvent } from "@/utils/posthog";
import { config } from "@common/utils/config";
import { formatWholeNumber, locale } from "@common/utils/i18n";
import {
  Box,
  Flex,
  Grid,
  Heading,
  Img,
  InfiniteQueryHandler,
  QueryHandler,
  Separator,
  Skeleton,
  Spinner,
  Text,
} from "@givenwell/components";
import { CalendarIcon, LocationPinIcon, ScreenIcon } from "@givenwell/core-solid-icons";
import {
  FavouriteListingMetadata,
  GetHomeControllerListingsResponseGroup,
  GetHomeControllerListingsResponseListing,
  GetHomeControllerListingsResponseParams,
  SearchForEapListingsRequest,
  SearchResult,
} from "@givenwell/marketplace-api";
import { styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { Suspense } from "react";
import { fallback, object, optional, string } from "valibot";
import { useSearchRadiusStore } from "../marketplace/useSearchRadius";
import { EAPClaimsCTA } from "./claims-cta";
import { RequestMoreSessionsButton } from "./RequestMoreSessionsButton";
import { SearchPanel } from "./SearchPanel";
import { UrgentCareButton } from "./UrgentCareButton";

export const Route = createFileRoute("/_auth/_app/eap/")({
  validateSearch: object({
    q: fallback(optional(string()), undefined),
    specialty: fallback(optional(string()), undefined),
    "location-type": fallback(optional(string()), undefined),
    gender: fallback(optional(string()), undefined),
    language: fallback(optional(string()), undefined),
  }),
  component: SafetyNet,
});

const route = getRouteApi("/_auth/_app/eap/");

const ListingGroupHeading = styled("h2", {
  fontScale: "md",
  fontFamily: fonts.ftHabit,
  weight: 700,
  lineHeight: "24px",
  "@sm": { fontSize: 20, lineHeight: "32px" },
});

function SafetyNet() {
  const search = route.useSearch();
  const { eap } = useMemberBenefits();

  const isSearching =
    !!search.q || !!search.specialty || !!search["location-type"] || !!search.gender || !!search.language;

  return (
    <PageWrapper topAppBar={<TopAppBar right={<UrgentCareButton />} />}>
      <PageInset
        css={{
          "@md": { pt: 0 },
          "@lg": {
            pt: 0,
          },
        }}
      >
        <Box css={{ width: "100%", maxW: 2000, mx: "auto" }}>
          <IntroStatusBar />
          <Flex
            css={{
              flexDirection: "column-reverse",
              "@lg": { flexDirection: "row", gap: "var(--layout-margin)" },
            }}
          >
            <Box
              css={{
                "@lg": {
                  flex: "3 0 0px",
                  // flex: "4 0 0px",

                  overflowX: "hidden",
                },
                "@xl": { flex: "5 0 0px" },
              }}
            >
              <SearchPanel />
              {/* <Box css={{ height: 16 }} /> */}
              {isSearching ? (
                <EAPCardGrid isSearching={isSearching} />
              ) : (
                <>
                  <PurchasesSection />
                  <RecommendedListingsFeed />
                </>
              )}
            </Box>
            {eap?.canSubmitClaims && (
              <Box
                css={{
                  flex: "2 0 0px",
                  "@md": {
                    flex: "1.5 0 0px",
                    maxW: 500,
                  },
                  "@lg": {
                    flex: "1.25 0 0px",
                  },
                  "@xl": { flex: "1 0 0px" },
                }}
              >
                <Flex
                  css={{
                    flexDirection: "column",
                    gap: 16,
                    "@lg": {
                      position: "sticky",
                      top: "var(--layout-margin)",
                      minHeight: "calc(100vh - var(--safe-area-inset-top) - var(--safe-area-inset-bottom) - 52px)",
                      pb: "var(--layout-margin)",
                    },
                  }}
                >
                  <Suspense>
                    <EAPClaimsCTA />
                  </Suspense>
                </Flex>
              </Box>
            )}
          </Flex>
        </Box>
      </PageInset>
    </PageWrapper>
  );
}

/**
 * Show the simplified home feed if no theme is populated enough to hit the size limit.
 */
function shouldShowSimplifiedHomeFeed(homeFeed: GetHomeControllerListingsResponseGroup[]) {
  return homeFeed.every(group => group.listings.length < 25);
}

function RecommendedListingsFeed() {
  const searchRadius = useSearchRadiusStore(state => state.searchRadius);
  const listingGroupsQuery = useQuery(makeHomeQueryOptions(searchRadius, "eap"));

  return (
    <Box css={{ pt: 16 }}>
      <QueryHandler
        query={listingGroupsQuery}
        success={({ data }) => (
          <div
            style={{
              position: "relative",
              width: "100%",
            }}
          >
            {shouldShowSimplifiedHomeFeed(data) ? (
              <SimplifiedHomeFeed data={data} />
            ) : (
              <>
                {data.map(virtualItem => {
                  const group = virtualItem;
                  return (
                    <ListingCardShelf
                      key={group.name}
                      groupName={group.name}
                      params={group.params}
                      searchResults={group.listings}
                    />
                  );
                })}
              </>
            )}
          </div>
        )}
        pending={() => <></>}
      />
    </Box>
  );
}

function dedupeListings(listings: GetHomeControllerListingsResponseListing[]) {
  return listings.filter((a, i) => listings.findIndex(b => a.listing_id === b.listing_id) === i);
}

function SimplifiedHomeFeed({ data }: { data: GetHomeControllerListingsResponseGroup[] }) {
  return (
    <ListingCardGrid>
      {dedupeListings(data.flatMap(p => p.listings)).map((item, i) => (
        <EAPListingCard listing={item} key={i} />
      ))}
    </ListingCardGrid>
  );
}

type ListingCardShelfProps = {
  groupName: string;
  params?: GetHomeControllerListingsResponseParams;
  searchResults: Array<SearchResult | GetHomeControllerListingsResponseListing>;
  favouritesMetadata?: FavouriteListingMetadata[];
};
function ListingCardShelf(props: ListingCardShelfProps) {
  const categoryIds = props.params?.category_ids ?? [];
  const variantTypes = props.params?.variant_types ?? [];
  const hasSearchParams = categoryIds.length > 0 || variantTypes.length > 0;

  const hasMore =
    props.params && (categoryIds.length > 0 || variantTypes.length > 0) && props.searchResults.length > 24;

  return (
    <Shelf
      title={props.groupName}
      enableSeeMore={hasMore}
      seeMoreLinkOptions={{
        to: "/marketplace",
        search: {
          category: hasSearchParams && categoryIds.length > 0 ? categoryIds : undefined,
          "variant-type": hasSearchParams && variantTypes.length > 0 ? variantTypes : undefined,
        },
      }}
      onSeeMore={() => {
        captureCustomEvent("member_filtered_by_theme", {
          theme_name: props.groupName,
        });
      }}
    >
      {props.searchResults.slice(0, 24).map((listing, i) => (
        <EAPListingCard key={i} listing={listing} />
      ))}
    </Shelf>
  );
}

function ShelfSkeleton({ size }: { size?: "big" }) {
  return (
    <Shelf
      size={size}
      title={
        <Heading css={{ fontScale: "md", color: colors.blue800 }}>
          <Skeleton>Something</Skeleton>
        </Heading>
      }
    >
      {[1, 2, 3, 4, 5, 6].map(listing => (
        <ListingCardSkeleton key={listing} />
      ))}
    </Shelf>
  );
}

const numberWords = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
function formatSessionCount(num: number) {
  return numberWords[num] || num.toString();
}

function IntroStatusBar() {
  const { eap } = useMemberBenefits();

  return (
    <Box
      css={{
        marginBottom: 16,
      }}
    >
      <ColoredCard color="cyan">
        <Grid
          css={{
            position: "relative",
            alignItems: "center",
            columnGap: 12,
            rowGap: 12,
            flexDirection: "column-reverse",
            gridTemplateAreas: `"title balance" "body body"`,
            gridTemplateColumns: "1fr auto",

            "@sm": {
              flexDirection: "row",
              alignItems: "flex-start",
              gridTemplateAreas: `"title balance" "body balance"`,
            },
          }}
        >
          <Text
            css={{
              fontFamily: fonts.ftHabit,
              fontScale: "2xl",
              weight: 600,
              lineHeight: 1.1,
              gridArea: "title",
              textWrap: "balance",
            }}
          >
            Chat with someone, privately, for free.
          </Text>
          <Box css={{ gridArea: "body", spaceY: 12 }}>
            {(eap?.availableSessions ?? 0) > 0 ? (
              <Text css={{ maxWidth: "60ch", fontScale: "sm" }}>
                You can book up to {formatSessionCount(eap?.availableSessions || 0)} sessions with one of the Givenwell
                providers below
                {eap?.canSubmitClaims && ", or claim back the cost of someone else you prefer to work with"}.
              </Text>
            ) : (
              <Text css={{ maxWidth: "60ch", fontScale: "sm" }}>You do not have any remaining free sessions.</Text>
            )}
            {eap?.canSubmitClaims && (
              <ButtonLink
                variant="filled"
                color="neutralLight"
                to="/claims"
                css={{
                  "@md": {
                    display: "none",
                  },
                }}
              >
                Claim your own provider
              </ButtonLink>
            )}
            <RequestMoreSessionsButton />
          </Box>

          <Box
            css={{
              gridArea: "balance",
              backgroundColor: colors.cyanLight,
              borderRadius: 8,
              padding: 8,
              px: 12,
              textAlign: "center",
              spaceY: 6,
              "@sm": { textAlign: "left" },
            }}
          >
            <Text css={{ fontScale: "2xl", weight: 500, lineHeight: 1 }}>{eap?.availableSessions || 0}</Text>
            <Text css={{ fontScale: "xs", lineHeight: 1.3, width: "min-content", "@sm": { width: "max-content" } }}>
              Sessions available
            </Text>
          </Box>
        </Grid>
      </ColoredCard>
    </Box>
  );
}

const dateFormatter = new Intl.DateTimeFormat(locale, {
  month: "short",
  day: "numeric",
});
const timeFormatter = new Intl.DateTimeFormat(locale, {
  hour: "numeric",
  minute: "2-digit",
});

function PurchasesSection() {
  const userId = useUserId();
  const { data } = useInfiniteQuery(infinitePurchaseHistoryQueryOptions(userId, undefined, "eap"));
  const purchases = data?.pages.flat().filter(p => p.purchase_status !== "cancelled");

  const dedupedPurchases = purchases?.filter((p, i) => {
    const existingIndex = purchases.findIndex(p2 => p2.listing_id === p.listing_id);
    return existingIndex === i;
  });

  if (!dedupedPurchases?.length) {
    return null;
  }

  return (
    <Box>
      <ListingGroupHeading css={{ pb: 12 }}>Your Bookings</ListingGroupHeading>
      <ListingCardGrid>
        {dedupedPurchases.map(purchase => (
          <PurchaseDialog key={purchase.purchase_id} purchaseId={purchase.purchase_id} placeholderData={purchase}>
            <Box>
              <Box css={{ position: "relative" }}>
                <Img
                  src={purchase?.media_url || undefined}
                  srcSet={purchase?.media_url ? generateSrcSet(purchase.media_url) : undefined}
                  sizes={"auto, (min-width: 600px) 200px, 50vw"}
                  alt={purchase?.listing_title}
                  css={{
                    objectFit: "cover",
                    objectPosition: "center",
                    w: "100%",
                    aspectRatio: 1,
                    rounded: 12,
                  }}
                  loading="lazy"
                />
                {purchase.starts_at && purchase.purchase_status === "upcoming" && (
                  <Flex
                    css={{
                      position: "absolute",
                      bottom: 6,
                      right: 6,
                      gap: 6,
                      items: "center",
                      backgroundColor: colors.white,
                      boxShadow: "0 0 8px rgba(0, 0, 0, 0.6)",
                      color: colors.navy,
                      borderRadius: 12 - 6,
                      px: 4,
                      weight: 500,
                    }}
                  >
                    <CalendarIcon width="11" height="11" color={colors.text} />

                    <Text css={{ fontSize: 12, flex: "1 0 0px" }}>
                      {timeFormatter.format(new Date(purchase.starts_at))},{" "}
                      {dateFormatter.format(new Date(purchase.starts_at))}
                    </Text>
                  </Flex>
                )}
              </Box>
              <Text
                css={{
                  fontSize: 11,
                  weight: 500,
                  color: colors.text2,
                  w: "auto",
                  lineHeight: "18px",
                  lineClamp: 1,
                  pt: 6,
                }}
              >
                {purchase.byline}
              </Text>
              <Text
                css={{
                  fontScale: "sm",
                  lineClamp: 2,
                  weight: 500,
                  lineHeight: "18px",
                  mt: 2,
                  fontFamily: fonts.ftHabit,
                }}
              >
                {purchase.listing_title}
              </Text>

              <Box css={{ spaceY: 4, mt: 8 }}>
                {purchase.location_address && (
                  <Flex css={{ gap: 6, items: "start" }}>
                    <LocationPinIcon width="11" height="11" color={colors.text2} style={{ marginTop: 4 }} />

                    <Text css={{ fontSize: 11, flex: "1 0 0px" }}>{purchase.location_address.compact}</Text>
                  </Flex>
                )}
                {purchase.location_type === "online" && (
                  <Flex css={{ gap: 6, items: "start" }}>
                    <ScreenIcon width="11" height="11" color={colors.text2} style={{ marginTop: 4 }} />

                    <Text css={{ fontSize: 11, flex: "1 0 0px" }}>Online</Text>
                  </Flex>
                )}
              </Box>
            </Box>
          </PurchaseDialog>
        ))}
      </ListingCardGrid>
      <Separator />
    </Box>
  );
}

function EAPCardGrid({ isSearching }: { isSearching?: boolean }) {
  const searchRadius = useSearchRadiusStore(state => state.searchRadius);
  const searchParams = route.useSearch();
  const { specialty, q, "location-type": locationType, gender, language } = searchParams;
  const locationTypes = config.features.onlineListingsOnly
    ? ["online" as LocationType, "phone" as LocationType]
    : !locationType
      ? []
      : locationType.split(",").map(type => type.trim() as LocationType);

  const tags = [
    specialty ? { exclude: false, ids: specialty ? [specialty] : [] } : undefined,
    gender ? { exclude: false, ids: gender ? [gender] : [] } : undefined,
    language ? { exclude: false, ids: language ? [language] : [] } : undefined,
  ].filter(Boolean) as { exclude: boolean; ids: string[] }[];

  const args: SearchForEapListingsRequest = {
    search_text: q || null,
    search_radius_meters: searchRadius,
    location_types: locationTypes,
    tags: tags.length > 0 ? tags : undefined,
  };

  const query = useInfiniteQuery(infiniteSearchForEAPListingsQueryOptions(args));

  const expandedSearchArgs = {
    ...args,
  };
  if (q?.trim()) {
    expandedSearchArgs.search_text = null;
  } else if (locationTypes.length > 0) {
    expandedSearchArgs.location_types = undefined;
  } else if (tags[0]) {
    expandedSearchArgs.tags = [...args.tags!];
    let firstTag = expandedSearchArgs.tags[0]!;
    firstTag = {
      ...firstTag,
      exclude: true,
    };
    expandedSearchArgs.tags[0] = firstTag;
  }

  return (
    <InfiniteQueryHandler
      query={query}
      success={({ data }) => {
        const isEmpty = data.pages.flatMap(p => p.items).length === 0;

        if (isEmpty) {
          return (
            <>
              <EmptyState />
              {!query.hasNextPage && <OtherSearchOptions queryArgs={expandedSearchArgs} />}
            </>
          );
        }

        return (
          <>
            {isSearching && data.pages[0] && (
              <Flex css={{ items: "center", py: 16 }}>
                <Text css={{ fontScale: "sm", weight: 500 }}>
                  {formatWholeNumber(data.pages[0].total_items)}{" "}
                  {data.pages[0].total_items === 1 ? "result" : "results"}
                </Text>
                <Box css={{ flex: "1 0 0px" }} />
                <FlexLink
                  css={{
                    h: 32,
                    px: 12,
                    fontScale: "xs",
                    weight: 500,
                    backgroundColor: colors.warmGrey,
                    items: "center",
                    borderRadius: 9999,
                  }}
                  to="/eap"
                >
                  Reset
                </FlexLink>
              </Flex>
            )}
            {specialty && <ListingGroupHeading css={{ mb: 16 }}>Specialised people</ListingGroupHeading>}
            <ListingCardGrid>
              {data.pages
                .flatMap(p => p.items)
                .map((item, i) => (
                  <EAPListingCard listing={item} key={i} />
                ))}
              {query.hasNextPage && (
                <InfiniteScrollEnd
                  onReach={() => {
                    if (query.hasNextPage) {
                      query.fetchNextPage();
                    }
                  }}
                >
                  {query.isFetchingNextPage && <Spinner />}
                </InfiniteScrollEnd>
              )}
            </ListingCardGrid>
            {!!specialty && !query.hasNextPage && <OtherSearchOptions queryArgs={expandedSearchArgs} />}
          </>
        );
      }}
      pending={() => (
        <>
          {/* <Flex css={{ py: 16 }}>
            <Skeleton>123 results</Skeleton>
          </Flex> */}

          <ListingCardGrid>
            {Array.from({ length: 24 }).map((_, i) => (
              <EAPListingCardSkeleton key={i} />
            ))}
          </ListingCardGrid>
        </>
      )}
    />
  );
}
function OtherSearchOptions({ queryArgs }: { queryArgs: SearchForEapListingsRequest }) {
  const query = useInfiniteQuery(infiniteSearchForEAPListingsQueryOptions(queryArgs));

  return (
    <>
      <Separator css={{ my: 16 }} />
      <ListingGroupHeading css={{ mb: 16 }}>Other options</ListingGroupHeading>
      <InfiniteQueryHandler
        query={query}
        success={({ data }) => {
          const isEmpty = data.pages.flatMap(p => p.items).length === 0;

          if (isEmpty) {
            return <EmptyState />;
          }

          return (
            <>
              <ListingCardGrid>
                {data.pages
                  .flatMap(p => p.items)
                  .map((item, i) => (
                    <EAPListingCard listing={item} key={i} />
                  ))}
                {query.hasNextPage && (
                  <InfiniteScrollEnd
                    onReach={() => {
                      if (query.hasNextPage) {
                        query.fetchNextPage();
                      }
                    }}
                  >
                    {query.isFetchingNextPage && <Spinner />}
                  </InfiniteScrollEnd>
                )}
              </ListingCardGrid>
            </>
          );
        }}
        pending={() => (
          <>
            <ListingCardGrid>
              {Array.from({ length: 24 }).map((_, i) => (
                <EAPListingCardSkeleton key={i} />
              ))}
            </ListingCardGrid>
          </>
        )}
      />
    </>
  );
}

function EmptyState() {
  const { eap } = useMemberBenefits();
  const canSubmitClaims = eap?.canSubmitClaims ?? false;
  return (
    <Flex
      css={{
        flexDirection: "column",
        py: 16,
        gap: 16,
        align: "center",
        width: "100%",
        maxW: 400,
        mx: "auto",
      }}
    >
      <Heading css={{ fontScale: "2xl", lineHeight: 1.1, fontFamily: fonts.ftHabit }}>No results</Heading>
      <Text css={{ fontScale: "sm" }}>
        Sorry, we couldn't find any results matching your criteria.
        {canSubmitClaims &&
          " If there's someone in particular you have in mind, you may be able to claim back the cost of your sessions with them."}
      </Text>

      {canSubmitClaims && (
        <ButtonLink
          variant="filled"
          // color="neutralLight"
          size="lg"
          to="/claims"
        >
          Claim your own provider
        </ButtonLink>
      )}
      <ButtonLink to="/eap" variant="filled" size="lg" color="pink">
        Clear Search
      </ButtonLink>
    </Flex>
  );
}

const ListingCardGrid = styled("div", {
  display: "grid",
  gridCols: 2,
  gap: 16,
  pb: 16,
  "@xs": {
    gridCols: 1,
  },
  "@sm": {
    gridCols: 3,
  },
  "@md": {
    gridCols: 3,
  },
  "@lg": {
    gridCols: 4,
  },
  "@xl": {
    gridCols: 5,
  },
});
